/** Feature flags for the dashboard. */

function falsy(value) {
    const s = String(value).trim().toLowerCase();
    return s === "0" || s === "false" || s === "no" || s === "off";
}

// Default landing surface when no ?mode= is set. The param-less "dashboard"
// home (canvas list + chat hub) is the default; this is the fast revert lever
// for the explore→dashboard front-door change — no rebuild:
//
//   localStorage.setItem("dashboard:landing", "explore"); location.reload();
//
// or pin at build time with VITE_DEFAULT_LANDING=explore. Reverting to
// "explore" turns the Dashboard mode fully off — App drops it from the rail
// and from the set of reachable modes, not just the default (see App.jsx
// dashboardEnabled). Local storage overrides the environment. Only an
// explicit "explore" reverts; anything else (or
// unset) keeps the dashboard default.
const LANDING_ENV =
    typeof import.meta !== "undefined" &&
    typeof import.meta.env?.VITE_DEFAULT_LANDING === "string"
        ? import.meta.env.VITE_DEFAULT_LANDING
        : null;

let _cachedLanding = null;

export function defaultLanding() {
    if (_cachedLanding !== null) return _cachedLanding;
    let ls = null;
    try {
        if (typeof localStorage !== "undefined") ls = localStorage.getItem("dashboard:landing");
    } catch {
        ls = null;
    }
    const source = ls != null ? ls : LANDING_ENV;
    _cachedLanding = String(source || "").trim().toLowerCase() === "explore" ? "explore" : "dashboard";
    return _cachedLanding;
}

// Cross-tab comparison surfaces — the Contrast panel (endpoints × clusters
// attribution grid in the Explore right column) and the Cluster swimlane lens
// in the activity surface. Hidden by default; shown only per-browser:
//
//   localStorage.setItem("dashboard:comparisons", "1"); location.reload();
//
// This is localStorage-only (no VITE_* env / build pin), strict "1", not swept
// by the legacy `origin.feature.*` wildcard, and read
// once at module load (a flip needs a reload). With this off, Explore context mode has
// no Contrast filler, so the right column shows chat unless a specific detail
// is mounted (see ScopedPage.jsx).
let _cachedComparisons = null;

export function comparisonsEnabled() {
    if (_cachedComparisons !== null) return _cachedComparisons;
    let on = false;
    try {
        if (typeof localStorage !== "undefined") on = localStorage.getItem("dashboard:comparisons") === "1";
    } catch {
        on = false;
    }
    _cachedComparisons = on;
    return _cachedComparisons;
}

const TRACE_CAPTURE_SOURCE_MODES = new Set(["development", "devint", "docker", "staging"]);

export function traceCaptureSourceEnabled() {
    const env = (typeof import.meta !== "undefined" && import.meta.env) || {};
    const mode = String(env.MODE || "").trim().toLowerCase();
    if (TRACE_CAPTURE_SOURCE_MODES.has(mode)) return true;

    const apiBase = String(env.VITE_API_BASE_URL || "").trim();
    try {
        const host = new URL(apiBase).hostname.toLowerCase();
        return host === "localhost" || host === "127.0.0.1" || host.includes(".devint.") || host.includes(".staging.");
    } catch {
        return false;
    }
}

// Dims the analytics API can serve today. The four core PROMPT-side
// dims (agent, endpoint, cluster, session) plus tools recovered via
// the `PROMPT_TOOL_CALL_NAMES` virtual array field.
//
// `files` (PROMPT_RESOURCE_PATHS) stays parked until the backend
// carrying the planner-side retype is deployed. The descriptor now
// advertises ScalarType::ArrayOfString (group-by explodes via
// arrayJoin, IN-filter drilldowns match element-wise), so the only
// remaining gate is that deploy — re-add "files" here once it lands
// (api-server in shared envs; the analytics-test-server bin locally).
//
// `skills` sidesteps that block: it's a synthetic adapter dim (see
// skills.js) that LIKE-filters the resource-path array and slices the
// skill name client-side — never grouping/IN-filtering the array as a
// field — so it ships now while `files` waits. The pattern mirrors
// `mcp_servers` over the tool-name array.
//
// The remaining sidebar dims (hosts/urls/commands/projects) still
// require either a parser-derived column the proto doesn't expose or
// an entirely new entity. They stay hidden.
// `models` is sourced from the TOKEN entity
// (DIMENSION_FIELD_TOKEN_MODEL, rooted at ENTITY_TYPE_TOKEN — see
// executeModelEntries in apiClient.js).
export const ANALYTICS_SUPPORTED_DIM_PLURALS = new Set([
    "agents", "endpoints", "clusters", "sessions",
    "tools", "models",
    // Synthetic dim — skill names sliced client-side from
    // PROMPT_RESOURCE_PATHS by an `%skill%` heuristic. See skills.js +
    // skillAdapter.
    "skills",
    // Synthetic dim — per-user memory slugs sliced client-side from
    // PROMPT_RESOURCE_PATHS' `…/.claude/.../memory/<slug>.md`. See
    // memories.js + memoryAdapter.
    "memories",
    // Synthetic dim — entries are MCP server ids derived client-side
    // from PROMPT_TOOL_CALL_NAMES (`mcp__<server>__<tool>` convention).
    // See mcpServers.js + executeMcpServerDimensionEntries.
    "mcp_servers",
    // Directory identity (O-2400) — a first-class graph dim keyed on the
    // directory user assigned to each endpoint: DIM_INFO.identity, resolved via
    // the PROMPT → ENDPOINT_USER_ASSIGNMENT → DIRECTORY_USER `viaAssignment` chain.
    "identities",
    // Synthetic dim — entries are saved triggers evaluated CLIENT-SIDE over
    // hydrated prompts (no analytics source). Listed here only so the dim is
    // visible in the analytics-supported set; its data comes from
    // signalStore, not the analytics API. See signalsDim.js.
    "signals",
]);

// ── Auth-required (derived from env) ─────────────────────────────
//
// When `VITE_AUTH_REFRESH_URL` is set in the active env file, the dashboard:
//   - attaches `Authorization: Bearer <auth_token>` to every analytics
//     request (token read from localStorage — same keys origin's
//     ApiContext.jsx writes),
//   - silently refreshes via that URL on 401, retries once,
//   - issues *relative* `/v1/...` paths so Vite's proxy bridges to
//     `VITE_API_BASE_URL` (avoids CORS).
//
// When unset (default `envs/.env.development`), it skips auth and
// trusts the local gateway's tenant injection.
//
// The right env file is picked by `vite --mode <name>` matching
// `envs/.env.<name>`. Mode names mirror origin/dashboard's `envs/`.
export const AUTH_REQUIRED = Boolean(
    typeof import.meta !== "undefined" && import.meta.env?.VITE_AUTH_REFRESH_URL,
);

// Dev-proxy routing (see vite.config.js). Under `vite dev` against a
// REMOTE gateway, all `/v1/...` traffic goes same-origin through Vite's
// proxy to dodge the gateway's localhost CORS gap, so the client base is
// relative (empty) and the refresh URL falls back to a relative path
// (authBridge derives `${API_BASE_URL}/v1/auth/refresh`; authBootstrap's
// exchange derives the same from API_BASE_URL). In a production build
// `import.meta.env.DEV` is statically false, so this whole branch
// tree-shakes out and the absolute env URLs are used unchanged. A
// local-stack gateway (localhost) is left direct.
const _rawApiBase =
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) || "";
function _isRemoteGateway(url) {
    if (!/^https?:\/\//.test(url || "")) return false;
    try {
        return !/^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[?::1\]?)$/.test(new URL(url).hostname);
    } catch {
        return false;
    }
}
const DEV_PROXY =
    Boolean(typeof import.meta !== "undefined" && import.meta.env?.DEV) &&
    _isRemoteGateway(_rawApiBase);

export const AUTH_REFRESH_URL = DEV_PROXY
    ? ""
    : ((typeof import.meta !== "undefined" && import.meta.env?.VITE_AUTH_REFRESH_URL) || "");

export const API_BASE_URL = DEV_PROXY ? "" : (_rawApiBase || "http://localhost:8081");

// ── Self-hosted login (gated) ────────────────────────────────────
//
// When on, AUTH_REQUIRED bootstrap renders our own /login screen
// instead of redirecting to WorkOS's hosted page. The screen POSTs
// credentials to a gateway-side wrapper (see src/pages/LoginPage.jsx
// for the expected endpoint contracts). The SSO button on that page
// still uses the WorkOS redirect flow.
//
// Off by default — flip on once the gateway endpoints exist:
//   localStorage.setItem("dashboard:selfHostedLogin", "1"); location.reload();
//
// Or pin at build time with VITE_SELF_HOSTED_LOGIN=1 in the env file.
const SELF_HOSTED_LOGIN_ENV =
    typeof import.meta !== "undefined" &&
    typeof import.meta.env?.VITE_SELF_HOSTED_LOGIN === "string"
        ? import.meta.env.VITE_SELF_HOSTED_LOGIN
        : null;

function truthy(value) {
    const s = String(value).trim().toLowerCase();
    return s === "1" || s === "true" || s === "yes" || s === "on";
}

export function selfHostedLogin() {
    let ls = null;
    try {
        if (typeof localStorage !== "undefined") {
            ls = localStorage.getItem("dashboard:selfHostedLogin");
        }
    } catch {}
    const source = ls != null ? ls : SELF_HOSTED_LOGIN_ENV;
    return source != null && truthy(source);
}

// ── Inventory "fleet composition" seen surfaces (gated) ──────────
//
// The First seen / Last seen / Under management tabs on the Inventory
// composition card, plus the In scope / Inventoried / No snapshot header
// tiles. These read off metadata-change and endpoint-status data whose
// populations don't fully reconcile yet (O-2696: "first seen" is really the
// first metadata *change*, not enrollment; last-seen depends on the
// endpoint-status entity; the tiles count a different population than the
// table). The AI agents / Operating systems tabs and the endpoint table are
// NOT gated — only the questionable surfaces are.
//
// OFF by default so we don't expose numbers that read as fleet truth but
// aren't, while the source-side fix is in flight. Turn them on per-browser:
//
//   localStorage.setItem("dashboard:inventory", "1"); location.reload();
//
// or pin per-environment at build time with VITE_INVENTORY_SURFACES=1 (e.g.
// on for dogfood/devint, off for customer prod). Resolution order matches
// selfHostedLogin (localStorage overrides env); read once at module load, so a
// flip needs a reload.
const INVENTORY_SURFACES_ENV =
    typeof import.meta !== "undefined" &&
    typeof import.meta.env?.VITE_INVENTORY_SURFACES === "string"
        ? import.meta.env.VITE_INVENTORY_SURFACES
        : null;

let _cachedInventorySurfaces = null;

export function inventorySurfacesEnabled() {
    if (_cachedInventorySurfaces !== null) return _cachedInventorySurfaces;
    let ls = null;
    try {
        if (typeof localStorage !== "undefined") ls = localStorage.getItem("dashboard:inventory");
    } catch {
        ls = null;
    }
    const source = ls != null ? ls : INVENTORY_SURFACES_ENV;
    _cachedInventorySurfaces = source != null && truthy(source);
    return _cachedInventorySurfaces;
}

// ── OpenAI Compliance Logs integration (gated) ───────────────────
//
// The OpenAI card on Admin > Data Sources, where a tenant connects one
// Compliance Logs Platform key per workspace or org. OFF by default:
//
//   localStorage.setItem("dashboard:openaiIntegration", "1"); location.reload();
//
// or pin per-environment at build time with VITE_OPENAI_INTEGRATION=1.
//
// This gates the SETUP SURFACE, not capture. The `/v1/openai-integrations`
// endpoints and the `openai-sync` worker are ungated, so an operator can turn a
// beta tenant on by calling the create endpoint directly with this off — which is
// how the beta is reached. The trace capture-source label is deliberately NOT behind this
// flag for that reason; it rides `traceCaptureSourceEnabled` like every other
// capture source.
//
// Why it exists: the connector has never run against a real ChatGPT Enterprise
// tenant and we have no way to run one ourselves, so it cannot be finished before
// it ships — the record schemas, the discard counters and the per-cycle caps are
// all settled by someone else's data. The flag is how a named set of testers reach
// it without it being offered to every admin. Operated per-browser by whoever is
// told the key; a build pin turns it on for a whole environment, which is the
// blunter instrument and should be reserved for devint/staging.
//
// What removes it: a tenant that has actually run the connector, which closes the
// open questions in docs/projects/openai-compliance-connector.md. Delete the flag
// then rather than leaving it as permanent configuration.
const OPENAI_INTEGRATION_ENV =
    typeof import.meta !== "undefined" &&
    typeof import.meta.env?.VITE_OPENAI_INTEGRATION === "string"
        ? import.meta.env.VITE_OPENAI_INTEGRATION
        : null;

let _cachedOpenAiIntegration = null;

export function openAiIntegrationEnabled() {
    if (_cachedOpenAiIntegration !== null) return _cachedOpenAiIntegration;
    let ls = null;
    try {
        if (typeof localStorage !== "undefined") ls = localStorage.getItem("dashboard:openaiIntegration");
    } catch {
        ls = null;
    }
    const source = ls != null ? ls : OPENAI_INTEGRATION_ENV;
    _cachedOpenAiIntegration = source != null && truthy(source);
    return _cachedOpenAiIntegration;
}

// ── Legacy-parity feature gates ──────────────────────────────────
//
// The Skills view shipped behind a localStorage gate in the prior dashboard
// release (v1.1.0) and is carried forward unchanged so the same operator
// muscle memory works:
//
//   localStorage.setItem("origin.feature.skills", "1"); location.reload();
//
// Or flip the wildcard to enable every `origin.feature.*` gated view at
// once (useful for dev / demo machines):
//
//   localStorage.setItem("origin.feature.*", "1"); location.reload();
//
// Off by default. Keys deliberately reuse the legacy `origin.feature.*`
// namespace (not `dashboard:*`) so the dev console crib is the same one
// already in people's notes.

// ── Chat persistence (on by default, with a kill switch) ─────────
//
// Covers the server-sync layer AND the one-time IDB→PG backfill migration —
// nothing else. Turned OFF, the dashboard behaves bit-for-bit as it did before
// the feature existed: IndexedDB-only chat, no network sync, no backfill.
//
// Default ON. The feature is complete and verified end to end in staging, and
// root AGENTS.md → "Feature Delivery and Gating" says a finished feature ships
// enabled rather than sitting behind an opt-in. What remains is not "is it
// ready" but "can it be stopped without a redeploy" — first activation is the
// one moment that matters, because every open browser begins syncing and runs
// its backfill at once, and that is a shape no amount of staging exercises.
//
// So this stays as an OFF switch rather than being deleted:
//   localStorage.setItem("dashboard:chatPersistence", "0"); location.reload();
// or pin a whole environment off at build time with VITE_CHAT_PERSISTENCE=0.
// Resolution order matches the other gates (localStorage overrides env); only an
// explicit falsy value disables, so an unset flag — every browser today — is on.
//
// Read LIVE, not cached: turning it off has to take effect at the next sync
// boundary, without waiting for every tab to reload. That is the whole point of
// keeping it, so do not "optimize" this into a module-load constant.
//
// Removal condition, unchanged in substance from when this was an opt-in: delete
// the flag and its call sites once chat persistence has run a full release in
// prod with the switch never used. Removing it before that trades a
// seconds-fast revert for a dashboard rollback.
const CHAT_PERSISTENCE_ENV =
    typeof import.meta !== "undefined" &&
    typeof import.meta.env?.VITE_CHAT_PERSISTENCE === "string"
        ? import.meta.env.VITE_CHAT_PERSISTENCE
        : null;

export function chatPersistenceEnabled() {
    let ls = null;
    try {
        if (typeof localStorage !== "undefined") ls = localStorage.getItem("dashboard:chatPersistence");
    } catch {
        ls = null;
    }
    const source = ls != null ? ls : CHAT_PERSISTENCE_ENV;
    return source == null ? true : !falsy(source);
}

// ── Security tab (gated, off by default) ─────────────────────────
//
// The Security rail entry and its three views: Overview, Discovery and
// Investigations. OFF by default. Turn it on per-browser:
//
//   localStorage.setItem("dashboard:securityTab", "1"); location.reload();
//
// or pin a whole environment on at build time with VITE_SECURITY_TAB=1 in its
// envs/.env.<mode> file. Resolution order matches the other gates
// (localStorage overrides env); only an explicit truthy value shows the tab.
//
// Read once at module load: AdminMenu filters ADMIN_VIEWS at load through the
// `gate` field, and App derives its valid modes from that list, so a flip
// needs a reload. With the tab off, the rail entry is absent and
// ?mode=security, ?adminView=discovery|alerts|security-overview and the legacy
// ?view= forms all fall through to the default landing.
//
// Operator: whoever owns the dashboard build for the environment. Removal:
// delete the flag and its call sites once the tab is on for every environment.
const SECURITY_TAB_ENV =
    typeof import.meta !== "undefined" &&
    typeof import.meta.env?.VITE_SECURITY_TAB === "string"
        ? import.meta.env.VITE_SECURITY_TAB
        : null;

let _cachedSecurityTab = null;

export function securityTabEnabled() {
    if (_cachedSecurityTab !== null) return _cachedSecurityTab;
    let ls = null;
    try {
        if (typeof localStorage !== "undefined") ls = localStorage.getItem("dashboard:securityTab");
    } catch {
        ls = null;
    }
    const source = ls != null ? ls : SECURITY_TAB_ENV;
    _cachedSecurityTab = source != null && truthy(source);
    return _cachedSecurityTab;
}

// -- Product analytics (PostHog), gated, off by default -----------------
//
// Sends dashboard usage and frontend errors to PostHog. On only when the
// environment's build pins VITE_PRODUCT_ANALYTICS=1 in envs/.env.<mode>, and
// even then nothing initializes unless the build also carries
// VITE_POSTHOG_KEY and VITE_POSTHOG_HOST. There is deliberately no
// per-browser override: whether an environment sends is decided by its
// build configuration alone, and lib/analytics/client.js also refuses
// development builds and local hosts.
//
// Reason: sending usage data to a third party is a privacy boundary that must
// be a per-environment decision. Safe default: off. Operator: the engineering
// and infrastructure owners of the environment's build, through the env file
// pin and the Forgejo secret that supplies the key. Removal: the production
// go decision in docs/projects/dashboard-product-analytics.md, after which key
// presence alone gates init and this flag is deleted.
const PRODUCT_ANALYTICS_ENV =
    typeof import.meta !== "undefined" &&
    typeof import.meta.env?.VITE_PRODUCT_ANALYTICS === "string"
        ? import.meta.env.VITE_PRODUCT_ANALYTICS
        : null;

export function productAnalyticsEnabled() {
    return PRODUCT_ANALYTICS_ENV != null && truthy(PRODUCT_ANALYTICS_ENV);
}

function legacyGate(key) {
    try {
        if (typeof localStorage === "undefined") return false;
        // Master switch — flip `origin.feature.*` to enable every legacy
        // gate at once without setting each key individually. Scoped to
        // the `origin.feature.*` namespace; doesn't affect `dashboard:*`
        // flags like selfHostedLogin.
        if (localStorage.getItem("origin.feature.*") === "1") return true;
        return localStorage.getItem(key) === "1";
    } catch {
        return false;
    }
}

// Skills v1 was hidden in v1.1.0 (O-871, O-870). Re-enable per-user.
export function skillsFeatureEnabled() {
    return legacyGate("origin.feature.skills");
}

// Agent Policy is hidden until it is ready for customers. Enable per-user:
//   localStorage.setItem("origin.feature.agent-policy", "1");
export function agentPolicyFeatureEnabled() {
    return legacyGate("origin.feature.agent-policy");
}

// Chat auto-dock: while on Explore, ⌘K snaps the chat into the right
// column (switch to Chat + un-collapse + focus) instead of opening the
// floating overlay. Experimental — OFF by default, so ⌘K opens the
// familiar overlay everywhere (the Context⇄Chat rail switch still docks
// the chat manually regardless). Enable per-user:
//   localStorage.setItem("origin.feature.chat-autodock", "1");
// Read at keypress time, so no reload is needed for it to take effect.
export function chatAutoDockEnabled() {
    return legacyGate("origin.feature.chat-autodock");
}

// Tenant-tailored starters override: when ON, a completed "Tailor these
// to your data" scout run REPLACES the generic fresh-chat starter pack
// (SAMPLE_QUERIES in ChatConsole) with its tenant-specific cards. OFF by
// default — so the curated default pack is never silently clobbered by a
// profiling run. With the flag off the scout is fully non-destructive:
// its tailored set still renders in the "Tenant profile" tab (read from
// that run's own transcript, not this store), so nothing is lost; the
// empty-state grid just keeps showing the defaults. Enable per-user:
//   localStorage.setItem("origin.feature.tailored-starters", "1");
// Read at render time, so a reload applies the change.
export function tailoredStartersEnabled() {
    return legacyGate("origin.feature.tailored-starters");
}
